import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, RefreshCw, FileSpreadsheet } from 'lucide-react';
import { toast } from 'react-toastify';
import { monthlyReportAPI } from '../../services/api';
import { can } from '../../services/session';
import { buildAmazonWorkbook, buildShopifyWorkbook, download } from './reportBuilder';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const label = (month) => { const [y, m] = month.split('-'); return `${MONTHS[Number(m) - 1]} ${y.slice(2)}`; };

export default function MonthlyReports() {
  const [month, setMonth] = useState(lastMonth());
  const [busy, setBusy] = useState(null);
  const [syncState, setSyncState] = useState({});
  const poll = useRef(null);
  const canSync = can('reports', 'edit') || can('amazon', 'edit');

  const loadStatus = useCallback(async () => {
    try { const r = await monthlyReportAPI.amazonSyncStatus(); setSyncState(r.data.stores || {}); return r.data.stores || {}; }
    catch { return {}; }
  }, []);
  useEffect(() => { loadStatus(); return () => clearInterval(poll.current); }, [loadStatus]);
  const syncing = Object.values(syncState).some(s => s.running);

  const refreshAmazon = async () => {
    try {
      await monthlyReportAPI.amazonSync(month);
      toast.info('Fetching Amazon transactions… this takes a few minutes');
      await loadStatus();
      clearInterval(poll.current);
      poll.current = setInterval(async () => {
        const st = await loadStatus();
        if (!Object.values(st).some(s => s.running)) {
          clearInterval(poll.current);
          const failed = Object.values(st).filter(s => s.error);
          failed.length ? toast.error('Amazon fetch failed: ' + failed.map(f => f.error).join('; ')) : toast.success('Amazon transactions are up to date');
        }
      }, 5000);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not start the Amazon fetch');
    }
  };

  const run = async (platform) => {
    setBusy(platform);
    try {
      if (platform === 'amazon') {
        const r = await monthlyReportAPI.amazon(month);
        if (!r.data.rows.length) { toast.warn(`No Amazon transactions for ${label(month)} yet — click "Fetch Amazon data" first`); return; }
        download(buildAmazonWorkbook(r.data), `Amazon Combined Summary ${label(month)}.xlsx`);
      } else {
        const r = await monthlyReportAPI.shopify(month);
        if (!r.data.rows.length) { toast.warn(`No Shopify orders for ${label(month)}`); return; }
        download(buildShopifyWorkbook(r.data), `Shopify ${label(month)} Working.xlsx`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not build the report');
    } finally {
      setBusy(null);
    }
  };

  const card = 'bg-white/5 border border-white/10 rounded-2xl p-6';
  const btn = 'px-4 py-2 rounded font-semibold flex items-center gap-2 disabled:opacity-50';
  return (
    <div className="max-w-4xl">
      <h1 className="text-3xl font-bold text-white mb-1">Monthly Reports</h1>
      <p className="text-gray-300 mb-6">Download a month's report in the same layout as your own sheets: store tabs, Working, Summary, Order, Refund, COGS, Supplier COGS, Profit and a Check tab.</p>

      <div className={card + ' mb-6 flex flex-wrap items-center gap-4'}>
        <label className="text-gray-300">Month</label>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
               className="bg-white/10 border border-white/20 rounded px-3 py-2 text-white" />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className={card}>
          <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2"><FileSpreadsheet size={20} /> Amazon</h2>
          <p className="text-gray-400 text-sm mb-4">GRS + Ibex: orders, refunds, fees, FBA, adjustments, payouts.</p>
          <button onClick={() => run('amazon')} disabled={!!busy} className={btn + ' bg-white text-gray-900 mb-3'}>
            <Download size={16} /> {busy === 'amazon' ? 'Building…' : 'Download'}
          </button>
          {canSync && (
            <button onClick={refreshAmazon} disabled={syncing} className={btn + ' bg-white/10 text-white text-sm'}>
              <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Fetching…' : 'Fetch Amazon data for this month'}
            </button>
          )}
          {Object.entries(syncState).map(([id, s]) => (
            <p key={id} className="text-xs text-gray-400 mt-2">
              Store {id}: {s.running ? `fetching (${s.saved} lines)` : s.error ? `failed — ${s.error}` : `done (${s.saved} lines)`}
            </p>
          ))}
          <p className="text-xs text-gray-500 mt-3">Amazon data is fetched automatically every night. Use the button for a month that has not been fetched yet.</p>
        </div>

        <div className={card}>
          <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2"><FileSpreadsheet size={20} /> Shopify</h2>
          <p className="text-gray-400 text-sm mb-4">Orders by source (TikTok, web…), refunds, COGS.</p>
          <button onClick={() => run('shopify')} disabled={!!busy} className={btn + ' bg-white text-gray-900'}>
            <Download size={16} /> {busy === 'shopify' ? 'Building…' : 'Download'}
          </button>
        </div>

        <div className={card + ' opacity-60'}>
          <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2"><FileSpreadsheet size={20} /> eBay</h2>
          <p className="text-gray-400 text-sm">Coming next — needs eBay "Finances" access for the 5 stores.</p>
        </div>
      </div>
    </div>
  );
}
